import { Autocomplete, AutocompleteItem, AutocompleteProps } from '@heroui/react'
import { motion } from 'framer-motion'

interface Option {
  key: string
  label: string
  startContent?: React.ReactNode
}

type CustomAutocompleteProps = Omit<
  AutocompleteProps<Option>,
  'children' | 'defaultItems' | 'onSelectionChange' | 'onChange'
>

export interface SelectAutoCompleteProps extends CustomAutocompleteProps {
  label: string
  options: Option[]
  name?: string
  onChange?: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  customRenderValue?: (value: Option | undefined) => React.ReactNode
  value?: string
  errorMessage?: string
}

const SelectAutoComplete = ({
  label,
  options,
  name,
  onChange,
  onBlur,
  placeholder = '',
  customRenderValue,
  value,
  errorMessage,
  ...props
}: SelectAutoCompleteProps) => {
  return (
    <motion.div>
      <Autocomplete<Option>
        {...props}
        defaultItems={options}
        errorMessage={errorMessage}
        isClearable={false}
        isInvalid={Boolean(errorMessage)}
        label={label}
        labelPlacement="outside"
        name={name}
        placeholder={placeholder}
        radius="sm"
        selectedKey={value}
        variant="bordered"
        classNames={{
          popoverContent: 'bg-[#343537] border border-[#707070]',
        }}
        inputProps={{
          classNames: {
            label: [
              'text-foreground',
              'dark:text-foreground',
              'group-data-[filled-within=true]:text-foreground',
              'dark:group-data-[filled-within=true]:text-white',
              'group-data-[invalid=true]:text-danger',
              'dark:group-data-[invalid=true]:text-danger',
            ],
            input: [
              'bg-transparent',
              'text-foreground',
              'dark:text-white',
              'placeholder:text-default-400',
              'dark:placeholder:text-white/60',
              'group-data-[invalid=true]:placeholder:text-danger',
            ],
            innerWrapper: 'bg-transparent',
            inputWrapper: [
              'bg-default-50',
              'dark:bg-[#1e1e20]',
              'border-default-200',
              'dark:border-default-600',
              'hover:bg-default-100',
              'dark:hover:bg-[#1e1e20]/70',
              'group-data-[focus=true]:bg-default-100',
              'dark:group-data-[focus=true]:bg-[#1e1e20]/50',
              'group-data-[focus=true]:border-primary',
              'dark:group-data-[focus=true]:border-primary',
              '!cursor-text',
            ],
          },
        }}
        onBlur={onBlur}
        onClear={() => {
          onChange?.('')
        }}
        onSelectionChange={key => {
          onChange?.(key?.toString() || '')
        }}
      >
        {(item: Option) => (
          <AutocompleteItem key={item.key} textValue={item.label}>
            {customRenderValue ? customRenderValue(item) : item.label}
          </AutocompleteItem>
        )}
      </Autocomplete>
    </motion.div>
  )
}

export default SelectAutoComplete
